import { color, HStack, Text } from '@starrez/starryui9';
import React from 'react';
import styled from 'styled-components';
import { Button } from '../buttonBar/Button';
import { buildCountLabel, buildPageCaption, calculateTotalPages } from './utils';
import { ChevronStepLeft } from '../../images/icons/ChevronStepLeft';
import { ChevronLeft } from '../../images/icons/ChevronLeft';
import { ChevronRight } from '../../images/icons/ChevronRight';
import { ChevronStepRight } from '../../images/icons/ChevronStepRight';
import { strings } from '../../localisation';
import { useServerGridContext } from './ServerPageGridContext';
import { PaginationPerPage } from './PaginationPerPage';

export interface IPaginationProps {
    onClick: (skip: number, offset: number) => void;
    hasDropShadow: boolean;
    pageRecordCount?: number;
}

export const Pagination = ({ hasDropShadow, onClick, pageRecordCount = 0 }: IPaginationProps) => {
    const { currentPageCache, pageAmount, totalRecords = 0 } = useServerGridContext();

    const pageNumber = currentPageCache();
    const page_size = pageAmount();

    const skip = (pageNumber - 1) * page_size;

    const totalPages = calculateTotalPages(page_size, totalRecords);
    const countCaption = buildCountLabel(skip + 1, skip + pageRecordCount, totalRecords!);
    const pageCaption = buildPageCaption(pageNumber, totalPages);

    const handleNavigationClick = (pageNumber: number) => {
        currentPageCache(pageNumber);
        const skip = (pageNumber - 1) * page_size;
        onClick(skip, page_size);
    };

    const handlePageCount = (amount: number) => {
        const skip = (pageNumber - 1) * page_size;
        onClick(skip, amount);
        pageAmount(amount);
        currentPageCache(1);
    };

    return (
        <DropShadow hasDropShadow={hasDropShadow}>
            <HStack inline gap="xSmall" columns="auto auto 30px 30px auto 30px 30px">
                <Text variant="normal.regular" color={color.Grey.G80}>
                    {countCaption}
                </Text>

                <PaginationPerPage onClick={handlePageCount} value={page_size} />

                <Button
                    disabled={pageNumber === 1}
                    variant="small"
                    ariaLabel={strings.Action_MoveToTheFirstPage}
                    onClick={() => handleNavigationClick(1)}
                    title={strings.Action_MoveToTheFirstPage}
                >
                    <ChevronStepLeft />
                </Button>
                <Button
                    disabled={pageNumber === 1}
                    variant="small"
                    ariaLabel={strings.Action_MoveToThePreviousPage}
                    onClick={() => handleNavigationClick(pageNumber - 1)}
                    title={strings.Action_MoveToThePreviousPage}
                >
                    <ChevronLeft />
                </Button>
                <Text variant="normal.regular" color={color.Grey.G80}>
                    {pageCaption}
                </Text>
                <Button
                    disabled={totalPages === pageNumber}
                    variant="small"
                    ariaLabel={strings.Action_MoveToTheNextPage}
                    onClick={() => handleNavigationClick(pageNumber + 1)}
                    title={strings.Action_MoveToTheNextPage}
                >
                    <ChevronRight />
                </Button>
                <Button
                    disabled={totalPages === pageNumber}
                    variant="small"
                    ariaLabel={strings.Action_MoveToTheLastPage}
                    onClick={() => handleNavigationClick(totalPages)}
                    title={strings.Action_MoveToTheLastPage}
                >
                    <ChevronStepRight />
                </Button>
            </HStack>
        </DropShadow>
    );
};

const DropShadow = styled.div<{ hasDropShadow: boolean }>`
    box-shadow: ${(props) => (props.hasDropShadow ? '0px 2px 5px rgba(39, 40, 51, 0.2)' : '')};
    border-radius: 3px;
    padding: 4px 8px;
    position: sticky;
    right: 16px;
    bottom: -32px;
    background: ${(props) => (props.hasDropShadow ? 'white' : 'inherit')};
    float: right;
    transform: translateY(-100%);
`;
